/* <wishlist-slot>: the favourites heart beside the product page's buy button.
   The theme-rendered placeholder is the heart shoppers see; Klaviyo's Customer
   Hub control sits invisible in .klaviyo-wishlist-slot and supplies the saved
   state and the click target (see section-main-product.css).

   The slot is not in the HTML. It is added when window.overkillWishlistReady
   resolves (layout/theme.liquid: after the buy button is ready on a product
   page, at once elsewhere, e.g. quick view), or immediately when the heart is
   tapped first. Klaviyo's own observer mounts a late slot in ~0.3s. Before
   2026-11-03 the slot shipped in the HTML and this element polled every 150ms
   for up to 30s, each poll forcing a layout of the whole page while Klaviyo
   and PreProduct were still loading (~0.7s of main thread on a mid phone).
   Ported from Monster Fishing's placeholder bridge in assets/back-in-stock.js
   there. */
if (!customElements.get('wishlist-slot')) {
  customElements.define(
    'wishlist-slot',
    class WishlistSlot extends HTMLElement {
      // Klaviyo sizes its control by screen width, with rules the theme cannot
      // see. So the box learns: once the control has painted, its size is
      // stored per 100px band of viewport width, and the next page load at a
      // similar width applies it before Klaviyo lands. Until a width has been
      // seen, the CSS default (a button-height square) applies.
      static STORAGE_KEY = 'wishlist-slot-size';
      static BAND = 100;

      get band() {
        return Math.round(window.innerWidth / WishlistSlot.BAND) * WishlistSlot.BAND;
      }

      readSizes() {
        try {
          const parsed = JSON.parse(localStorage.getItem(WishlistSlot.STORAGE_KEY) || '{}');
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (error) {
          return {};
        }
      }

      applyStoredSize() {
        const sizes = this.readSizes();
        // Exact band first, else the nearest band within two bands either side.
        const bands = Object.keys(sizes)
          .map(Number)
          .filter((band) => Math.abs(band - this.band) <= WishlistSlot.BAND * 2)
          .sort((a, b) => Math.abs(a - this.band) - Math.abs(b - this.band));
        const size = bands.length ? sizes[bands[0]] : null;
        if (!size || !(size.w > 0) || !(size.h > 0)) return;
        this.style.setProperty('--wishlist-width', `${size.w}px`);
        this.style.setProperty('--wishlist-height', `${size.h}px`);
      }

      // Learns the control's size from a ResizeObserver, which reports it from
      // the browser's own layout pass. getBoundingClientRect here forced a
      // layout of the whole page mid-load (~180ms on a mid phone, 2026-11-03).
      storeSize(widget) {
        if (!('ResizeObserver' in window)) return;
        const ro = new ResizeObserver((entries) => {
          const box = entries[0] && entries[0].borderBoxSize && entries[0].borderBoxSize[0];
          const w = box ? box.inlineSize : entries[0].contentRect.width;
          const h = box ? box.blockSize : entries[0].contentRect.height;
          if (!(w > 8) || !(h > 8)) return;
          ro.disconnect();
          try {
            const sizes = this.readSizes();
            sizes[this.band] = { w: Math.round(w), h: Math.round(h) };
            localStorage.setItem(WishlistSlot.STORAGE_KEY, JSON.stringify(sizes));
          } catch (error) {
            /* storage unavailable: the CSS default carries the next load */
          }
        });
        ro.observe(widget);
        setTimeout(() => ro.disconnect(), 30000);
      }

      connectedCallback() {
        this.placeholder = this.querySelector('[data-wishlist-placeholder]');
        if (!this.placeholder) return;

        this.applyStoredSize();
        this.queued = false;
        this.placeholder.addEventListener('click', () => {
          if (this.check()) return;
          // Not ready: give Klaviyo its slot now and replay the tap once it lands.
          this.queued = true;
          this.ensureSlot();
        });

        // Server HTML cached from before the slot moved here still carries it.
        if (this.slot_) {
          this.watch();
          return;
        }
        (window.overkillWishlistReady || Promise.resolve()).then(() => this.ensureSlot());
      }

      get slot_() {
        return this.querySelector('.klaviyo-wishlist-slot');
      }

      ensureSlot() {
        if (this.slot_ || !this.isConnected) return;
        const slot = document.createElement('div');
        slot.className = 'klaviyo-wishlist-slot';
        slot.dataset.productId = this.dataset.productId || '';
        this.appendChild(slot);
        this.watch();
      }

      // Klaviyo mounts its control into the slot's light DOM and swaps the
      // heart's classes there, so one observer sees both the mount and later
      // saved/unsaved toggles. The interval is only a slow fallback in case a
      // mount ever lands without a light-DOM change (e.g. shadow DOM only).
      watch() {
        const slot = this.slot_;
        if (!slot || this.observer) return;
        this.syncSavedState();
        this.observer = new MutationObserver(() => {
          this.syncSavedState();
          this.check();
        });
        this.observer.observe(slot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
        if (this.check()) return;
        let tries = 0;
        this.timer = setInterval(() => {
          if (this.check() || ++tries > 20) this.stopTimer();
        }, 1000);
      }

      disconnectedCallback() {
        this.stopTimer();
        this.observer?.disconnect();
        this.observer = null;
      }

      syncSavedState() {
        const slot = this.slot_;
        if (!slot) return;
        const saved = !!slot.querySelector('.kl-hub-wishlist-icon--active');
        if (saved === this.classList.contains('is-saved')) return;
        this.classList.toggle('is-saved', saved);
        this.placeholder.setAttribute('aria-pressed', String(saved));
      }

      stopTimer() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }

      // Klaviyo's control is not guaranteed to be a <button>, so prefer its
      // custom element, then any interactive child, then the first child.
      get widget() {
        const slot = this.slot_;
        if (!slot) return null;
        return (
          slot.querySelector('klaviyo-favorites-button') ||
          slot.querySelector('button, a, [role="button"]') ||
          slot.querySelector(':scope > :not(style)')
        );
      }

      // Ready = the control has content, not merely been inserted: a populated
      // shadow root when readable, else children (Klaviyo's button holds its
      // heart icon), and only then a real box. offsetHeight forces a layout of
      // the whole page, so it is the last resort, as in card-wishlist.js.
      painted(host) {
        if (!host) return false;
        if (host.shadowRoot && host.shadowRoot.childElementCount > 0) return true;
        if (host.childElementCount > 0) return true;
        return host.offsetHeight > 8;
      }

      check() {
        if (this.classList.contains('wish-ready')) return true;
        const widget = this.widget;
        if (!this.painted(widget)) return false;

        this.classList.add('wish-ready');
        this.stopTimer();
        this.storeSize(widget);
        if (this.queued) {
          this.queued = false;
          widget.click();
        }
        return true;
      }
    }
  );
}
